import React from "react";
import { FixAndFlipForm } from "../../lib/models/short-term/short-term-form.model";
import {
  RentalForm,
  RentalPremierForm,
} from "../../lib/models/rental/rental-form.model";
import { QuoteUtils } from "../../lib/quote-utils";
import { QuoteMetadata } from "../../lib/types/QuoteMetadata";
import { CalculationType, QuotingType } from "../../state/quote-reducer";
import WarningDialog from "../WarningDialog";
import { RentalCalculation } from "../../lib/models/rental/rental-calculation.model";
import { FixAndFlipCalculation } from "../../lib/models/short-term/short-term-calculation.model";

export interface FormStaleQuoteDialogProps {
  quote: QuotingType;
  metadata: QuoteMetadata | undefined;
  calculateHandler: (quote: any) => void;
  calculation: CalculationType;
}

function FormStaleQuoteDialog(props: FormStaleQuoteDialogProps) {
  const { metadata, calculateHandler, quote, calculation } = props;
  const [isStaleDialogOpen, setStateDialogOpen] =
    React.useState<boolean>(false);

  const handleStaleModal = (timestamp: string, pricingLastUpdated: string) => {
    const dateLastUpdatedMilliseconds = Date.parse(timestamp.toString());
    const pricingLastUpdatedMilliseconds = Date.parse(
      pricingLastUpdated.toString()
    );
    if (dateLastUpdatedMilliseconds < pricingLastUpdatedMilliseconds) {
      setStateDialogOpen(true);
    }
  };

  const checkStaleQuote = React.useCallback(
    (metadata: QuoteMetadata, calculation: CalculationType) => {
      const { createdDate, updatedDate, pricingLastUpdated } = metadata;

      if (!createdDate) return;

      if (!pricingLastUpdated || !updatedDate) {
        setStateDialogOpen(true);
        return;
      }

      if (metadata?.rateLock?.rateLockActive) {
        return;
      }

      if (quote?.quoteType === "Rental") {
        const typedCalc = calculation as RentalCalculation;
        const timestamp = typedCalc.scenarios[0].pricingUpdateTimestamp;

        if (timestamp) {
          handleStaleModal(timestamp, pricingLastUpdated);
        }
      }

      if (quote?.quoteType === "STF") {
        const typedCalc = calculation as FixAndFlipCalculation;
        const timestamp = typedCalc.pricingUpdateTimestamp;

        if (timestamp) {
          handleStaleModal(timestamp, pricingLastUpdated);
        }
      }
    },
    [quote?.quoteType]
  );

  const handleCalculation = () => {
    calculateQuote(quote).then((quoteToCalculate) => {
      calculateHandler(quoteToCalculate);
    });
  };

  const calculateQuote = async (quote: QuotingType) => {
    if (quote === null) return;

    if (quote.quoteType === "Rental") {
      const formValues = new RentalForm(quote);
      const quoteTransform = await QuoteUtils.RentalFormToQuote(formValues);
      quoteTransform.scenario = QuoteUtils.GenerateRentalQuoteScenarios();
      return quoteTransform;
    } else if (quote.quoteType === "Premier") {
      const formValues = new RentalPremierForm(quote);
      const quoteTransform = await QuoteUtils.RentalFormToPremierQuote(
        formValues
      );
      quoteTransform.scenario = QuoteUtils.GenerateRentalQuoteScenarios();

      return quoteTransform;
    } else if (quote.quoteType === "STF") {
      const formValues = new FixAndFlipForm(quote);
      const quoteTransform = await QuoteUtils.FixAndFlipFormToQuote(formValues);

      return quoteTransform;
    }
  };

  React.useEffect(() => {
    if (metadata) {
      checkStaleQuote(metadata, calculation);
    }
  }, [metadata, checkStaleQuote, quote, calculation]);

  return (
    <WarningDialog
      confirmText="Recalculate"
      contentText="The calculations displayed in this quote may be stale and out of sync with the current pricing model."
      isOpen={isStaleDialogOpen}
      titleText="Recalculate Quote?"
      danger={false}
      onConfirmHandler={handleCalculation}
    />
  );
}

export default FormStaleQuoteDialog;
